import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DateTime } from 'luxon';
import { financialYearStartMonth, getFinancialYearsFromDate, TAX_REGION } from 'src/lib/tax';

type ReportPeriod = {
  title: string;
  value: string;
};

type Props = {
  region: TAX_REGION;
};

const useMarginTransactionsReport = ({ region }: Props = { region: 'AU' }) => {
  const [reportPeriod, setReportPeriod] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<DateTime>();
  const [dateTo, setDateTo] = useState<DateTime>();
  const { t } = useTranslation('margin');

  const reportingPeriods = useMemo((): ReportPeriod[] => {
    const periods: ReportPeriod[] = [];

    const financialYears = getFinancialYearsFromDate(new Date(2024, 1, 1), 'AU');

    periods.push(
      ...financialYears.map((year) => ({
        title: t('transactionReports.periods.financialYear', { years: year.replace('_', '-') }),
        value: year,
      })),
    );

    periods.push(
      {
        title: t('transactionReports.periods.thisMonth'),
        value: 'this_month',
      },
      {
        title: t('transactionReports.periods.lastMonth'),
        value: 'last_month',
      },
      {
        title: t('transactionReports.periods.custom'),
        value: 'custom',
      },
    );

    return periods;
  }, [t]);

  const isValid = useMemo(() => {
    if (!reportPeriod) return false;
    if (reportPeriod !== 'custom') return true;

    if (!dateFrom || !dateTo) return false;

    if (dateFrom > dateTo || dateTo < dateFrom) return false;

    return true;
  }, [dateFrom, dateTo, reportPeriod]);

  const reset = useCallback(() => {
    setDateFrom(undefined);
    setDateTo(undefined);
    setReportPeriod('');
  }, []);

  const startDate = useMemo(() => {
    if (!reportPeriod) return undefined;

    switch (reportPeriod) {
      case 'custom':
        return dateFrom;
      case 'this_month':
        return DateTime.now().startOf('month');
      case 'last_month':
        return DateTime.now().minus({ month: 1 }).startOf('month');
      default:
        // Try and parse to the financial year
        const [from] = reportPeriod.split('_');
        const fromYear = Number(from);

        if (isNaN(fromYear)) return undefined;

        const fromMonth = financialYearStartMonth[region] - 1;
        const fromDateTime = DateTime.fromJSDate(new Date(fromYear, fromMonth, 1)).startOf('month');

        return fromDateTime;
    }
  }, [dateFrom, region, reportPeriod]);

  const endDate = useMemo(() => {
    if (!reportPeriod) return undefined;

    const now = DateTime.now();

    switch (reportPeriod) {
      case 'custom':
        // if the dateTo is today, set dateTo to DateTime.now otherwise use the assigned date
        return dateTo?.hasSame(now, 'day') ? now : dateTo;
      case 'this_month':
        return now;
      case 'last_month':
        return now.minus({ month: 1 }).endOf('month');
      default:
        // Try and parse to the financial year
        const [, to] = reportPeriod.split('_');
        const toYear = Number(to);

        if (isNaN(toYear)) return undefined;

        const toMonth = financialYearStartMonth[region] - 2;
        const toDateTime = DateTime.fromJSDate(new Date(toYear, toMonth, 1)).endOf('month');

        return toDateTime > now ? now : toDateTime;
    }
  }, [dateTo, region, reportPeriod]);

  const timezone = DateTime.now().zone.name;

  return {
    reportingPeriods,
    reportPeriod,
    setReportPeriod,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    isValid,
    startDate,
    endDate,
    timezone,
    reset,
  };
};

export { useMarginTransactionsReport };
